import React, { useState, useCallback } from 'react';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ArrowRight,
  RefreshCw,
  Info,
  ChevronDown,
  ChevronUp,
  X,
  Loader2
} from 'lucide-react';
import { metricsImportAPI, ValidationResult, ImportResult, ROICalculation } from '../../services/metricsImportAPI';
import toast from 'react-hot-toast';

interface MetricsImportModalProps {
  projectId: number;
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

type Step = 'upload' | 'validate' | 'preview' | 'importing' | 'complete';

const MetricsImportModal: React.FC<MetricsImportModalProps> = ({
  projectId,
  isOpen,
  onClose,
  onImportComplete
}) => {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importUnmatched, setImportUnmatched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    engagement: true,
    labor: false,
    process: false,
    opportunity: true,
    matches: true,
    errors: true
  });

  const resetState = useCallback(() => {
    setStep('upload');
    setFile(null);
    setValidationResult(null);
    setImportResult(null);
    setImportUnmatched(false);
    setIsLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    const wasComplete = step === 'complete';
    resetState();
    onClose();
    if (wasComplete) {
      onImportComplete();
    }
  }, [resetState, onClose, onImportComplete, step]);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx')) {
        toast.error('Please select an Excel file (.xlsx)');
        return;
      }
      setFile(selectedFile);
      setStep('validate');
    }
  }, []);

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.xlsx')) {
        toast.error('Please select an Excel file (.xlsx)');
        return;
      }
      setFile(droppedFile);
      setStep('validate');
    }
  }, []);

  const handleValidate = useCallback(async () => {
    if (!file) return;

    setIsLoading(true);
    try {
      const result = await metricsImportAPI.validateFile(projectId, file);
      setValidationResult(result);
      setStep('preview');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Validation failed');
    } finally {
      setIsLoading(false);
    }
  }, [file, projectId]);

  const handleImport = useCallback(async () => {
    if (!file) return;

    setStep('importing');
    setIsLoading(true);

    try {
      const result = await metricsImportAPI.importFile(projectId, file, importUnmatched);
      setImportResult(result);
      setStep('complete');
      toast.success('Metrics imported successfully!');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Import failed');
      setStep('preview');
    } finally {
      setIsLoading(false);
    }

  }, [file, projectId, importUnmatched]);

  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-6 h-6" />
            <h2 className="text-xl font-semibold">Import Metrics Data</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center justify-center gap-2 py-3 px-4 bg-gray-50 border-b">
          {(['upload', 'validate', 'preview', 'importing', 'complete'] as Step[]).map((s, idx) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition-all ${
                step === s
                  ? 'bg-blue-600 text-white'
                  : idx < ['upload', 'validate', 'preview', 'importing', 'complete'].indexOf(step)
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-200 text-gray-500'
              }`}>
                {idx < ['upload', 'validate', 'preview', 'importing', 'complete'].indexOf(step) ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-xs">
                    {idx + 1}
                  </span>
                )}
                <span className="capitalize">{s}</span>
              </div>
              {idx < 4 && <ArrowRight className="w-4 h-4 text-gray-400" />}
            </React.Fragment>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === 'upload' && (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-blue-500 transition-colors cursor-pointer"
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <Upload className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-700 mb-2">
                Drop your metrics template here
              </p>
              <p className="text-sm text-gray-500 mb-4">
                or click to browse for an Excel file (.xlsx)
              </p>
              <input
                id="file-input"
                type="file"
                accept=".xlsx"
                onChange={handleFileSelect}
                className="hidden"
              />
              <div className="flex items-center justify-center gap-2 text-sm text-blue-600">
                <Info className="w-4 h-4" />
                <span>Use the "Download Template" button to get the correct format</span>
              </div>
            </div>
          )}

          {step === 'validate' && file && (
            <div className="text-center">
              <div className="bg-blue-50 rounded-lg p-6 mb-6 inline-block">
                <FileSpreadsheet className="w-16 h-16 mx-auto text-blue-600 mb-4" />
                <p className="font-medium text-gray-900">{file.name}</p>
                <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
              </div>

              <div className="flex justify-center gap-4">
                <button
                  onClick={() => {
                    setFile(null);
                    setStep('upload');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Choose Different File
                </button>
                <button
                  onClick={handleValidate}
                  disabled={isLoading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Validate File
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {step === 'preview' && validationResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-5 gap-3 mb-6">
                {[
                  { label: 'Engagement', value: validationResult.summary.engagement, color: 'blue' },
                  { label: 'Labor Rates', value: validationResult.summary.laborEconomics, color: 'green' },
                  { label: 'Processes', value: validationResult.summary.processMetrics, color: 'yellow' },
                  { label: 'Opportunities', value: validationResult.summary.opportunityMetrics, color: 'purple' },
                  { label: 'Benefits', value: validationResult.summary.nonFinancialBenefits, color: 'pink' }
                ].map(item => (
                  <div key={item.label} className={`bg-${item.color}-50 rounded-lg p-3 text-center`}>
                    <p className={`text-2xl font-bold text-${item.color}-600`}>{item.value}</p>
                    <p className="text-xs text-gray-600">{item.label}</p>
                  </div>
                ))}
              </div>

              {validationResult.validationErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg">
                  <button
                    onClick={() => toggleSection('errors')}
                    className="w-full flex items-center justify-between p-3 text-left"
                  >
                    <div className="flex items-center gap-2 text-red-700">
                      <XCircle className="w-5 h-5" />
                      <span className="font-medium">
                        {validationResult.validationErrors.length} Validation Error(s)
                      </span>
                    </div>
                    {expandedSections.errors ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {expandedSections.errors && (
                    <div className="px-3 pb-3">
                      <div className="bg-white rounded border divide-y max-h-40 overflow-y-auto">
                        {validationResult.validationErrors.map((error, idx) => (
                          <div key={idx} className="px-3 py-2 text-sm">
                            <span className="font-medium text-gray-700">{error.sheet}</span>
                            {error.row && <span className="text-gray-500"> (Row {error.row})</span>}
                            <span className="text-gray-500"> - {error.field}: </span>
                            <span className="text-red-600">{error.message}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-white border rounded-lg">
                <button
                  onClick={() => toggleSection('matches')}
                  className="w-full flex items-center justify-between p-3 text-left border-b"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Opportunity Matching</span>
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                      {validationResult.matchResults.matched} matched
                    </span>
                    {validationResult.matchResults.unmatched > 0 && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
                        {validationResult.matchResults.unmatched} unmatched
                      </span>
                    )}
                  </div>
                  {expandedSections.matches ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {expandedSections.matches && (
                  <div className="p-3 space-y-3">
                    {validationResult.matchResults.opportunityMatches.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Matched Opportunities:</p>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {validationResult.matchResults.opportunityMatches.map((match, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded">
                              <CheckCircle className={`w-4 h-4 ${match.isExact ? 'text-green-600' : 'text-yellow-600'}`} />
                              <span className="text-gray-600">{match.imported}</span>
                              <ArrowRight className="w-3 h-3 text-gray-400" />
                              <span className="font-medium text-gray-900">{match.matched}</span>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                match.isExact ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {match.similarity}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {validationResult.matchResults.unmatchedOpportunities.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Unmatched Opportunities:</p>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {validationResult.matchResults.unmatchedOpportunities.map((unmatched, idx) => (
                            <div key={idx} className="bg-yellow-50 px-3 py-2 rounded">
                              <div className="flex items-center gap-2 text-sm">
                                <AlertTriangle className="w-4 h-4 text-yellow-600" />
                                <span className="font-medium text-gray-900">{unmatched.name}</span>
                              </div>
                              {unmatched.suggestions.length > 0 && (
                                <p className="text-xs text-gray-500 mt-1 ml-6">
                                  Did you mean: {unmatched.suggestions.map(s => s.name).join(', ')}?
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                        <label className="flex items-center gap-2 mt-3 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={importUnmatched}
                            onChange={(e) => setImportUnmatched(e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          Import unmatched opportunities anyway (will be stored for future matching)
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {validationResult.parsedData.engagement && (
                <div className="bg-white border rounded-lg">
                  <button
                    onClick={() => toggleSection('engagement')}
                    className="w-full flex items-center justify-between p-3 text-left border-b"
                  >
                    <span className="font-medium text-gray-900">Engagement Setup</span>
                    {expandedSections.engagement ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {expandedSections.engagement && (
                    <div className="p-3 grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">Client:</span>
                        <span className="ml-2 font-medium">{validationResult.parsedData.engagement.client_name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Engagement:</span>
                        <span className="ml-2 font-medium">{validationResult.parsedData.engagement.engagement_name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Analyst:</span>
                        <span className="ml-2 font-medium">{validationResult.parsedData.engagement.analyst_name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Confidence:</span>
                        <span className="ml-2 font-medium">{validationResult.parsedData.engagement.data_confidence_level || '-'}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {validationResult.parsedData.laborEconomics.length > 0 && (
                <div className="bg-white border rounded-lg">
                  <button
                    onClick={() => toggleSection('labor')}
                    className="w-full flex items-center justify-between p-3 text-left border-b"
                  >
                    <span className="font-medium text-gray-900">
                      Labor Economics ({validationResult.parsedData.laborEconomics.length} roles)
                    </span>
                    {expandedSections.labor ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {expandedSections.labor && (
                    <div className="p-3">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Role</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-600">Hourly Rate</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-600">FTEs</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-600">Annual Hours</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {validationResult.parsedData.laborEconomics.map((labor, idx) => (
                            <tr key={idx}>
                              <td className="px-3 py-2">{labor.role_name}</td>
                              <td className="px-3 py-2 text-right">${labor.hourly_rate}</td>
                              <td className="px-3 py-2 text-right">{labor.fte_count}</td>
                              <td className="px-3 py-2 text-right">{labor.annual_hours}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 'importing' && (
            <div className="text-center py-12">
              <Loader2 className="w-16 h-16 mx-auto text-blue-600 animate-spin mb-4" />
              <p className="text-lg font-medium text-gray-700">Importing metrics data...</p>
              <p className="text-sm text-gray-500 mt-2">This may take a moment</p>
            </div>
          )}

          {step === 'complete' && importResult && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Import Complete!</h3>
                <p className="text-gray-600">{importResult.message}</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{importResult.summary.laborEconomics}</p>
                  <p className="text-sm text-gray-600">Labor Rates</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-purple-600">{importResult.summary.opportunityMetrics}</p>
                  <p className="text-sm text-gray-600">Opportunities</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{importResult.summary.opportunitiesUpdated}</p>
                  <p className="text-sm text-gray-600">Updated to Grounded</p>
                </div>
              </div>

              {importResult.roiCalculations && importResult.roiCalculations.length > 0 && (
                <div className="bg-white border rounded-lg">
                  <div className="p-3 border-b bg-gray-50 flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">Grounded ROI Calculations</h4>
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full font-medium">
                      ✓ Deterministic • v{importResult.roiCalculations[0]?.calculation_version || '1.0'}
                    </span>
                  </div>
                  <div className="p-3 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">Opportunity</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-600">Hours Saved/yr</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-600">Wtd. Rate</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-600">Annual Benefit</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-600">NPV (3yr)</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-600">ROI %</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-600">Payback</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {importResult.roiCalculations.map((roi, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium max-w-[200px] truncate" title={roi.opportunity_name}>
                              {roi.opportunity_name}
                            </td>
                            <td className="px-3 py-2 text-right text-blue-600">
                              {Number(roi.annual_hours_saved).toLocaleString(undefined, { maximumFractionDigits: 0 })} hrs
                            </td>
                            <td className="px-3 py-2 text-right text-gray-600">
                              ${Number(roi.weighted_hourly_rate).toFixed(0)}/hr
                            </td>
                            <td className="px-3 py-2 text-right text-green-600 font-medium">
                              {formatCurrency(Number(roi.total_annual_benefit))}
                            </td>
                            <td className="px-3 py-2 text-right text-purple-600">
                              {formatCurrency(Number(roi.npv_3year))}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                Number(roi.roi_3year_pct) > 200 ? 'bg-green-100 text-green-700' :
                                Number(roi.roi_3year_pct) > 100 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {Number(roi.roi_3year_pct).toFixed(0)}%
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right">
                              {roi.payback_months ? `${Number(roi.payback_months).toFixed(1)} mo` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            {step === 'complete' ? 'Close' : 'Cancel'}
          </button>

          {step === 'preview' && (
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setFile(null);
                  setValidationResult(null);
                  setStep('upload');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Start Over
              </button>
              <button
                onClick={handleImport}
                disabled={isLoading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Upload className="w-4 h-4" />
                Import Data
              </button>
            </div>
          )}

          {step === 'complete' && (
            <button
              onClick={handleClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetricsImportModal;
